<template>
    <!-- BaseConfirmDialog.vue -->
    <base-dialog :value="value" @click:outside="handleSecondaryFormEvent">
        <v-card class="mx-auto">
            <!-- Toolbar -->
            <base-form-toolbar
                :loading="loading"
                :title="title"
                @close="handleSecondaryFormEvent"
            ></base-form-toolbar>
            <!-- Content -->
            <base-form-container :loading="loading">
                <v-row class="no-gutters pa-3">
                    <!-- Form Errors -->
                    <v-col cols="12">
                        <base-form-alert
                            :errors="formErrorsAlert.content"
                            :value="formErrorsAlert.show"
                        ></base-form-alert>
                    </v-col>
                    <!-- Main Content-->
                    <template v-if="text">
                        <v-col cols="1">
                            <v-icon :color="textIconColor">{{ textIcon }}</v-icon>
                        </v-col>
                        <v-col class="pl-3 pb-3" cols="11">
                            <span class="text-body-1">{{ text }}</span>
                        </v-col>
                    </template>
                    <!-- Additional Info -->
                    <template v-for="item in infoItems">
                        <v-col :key="`${item}_icon`" cols="1">
                            <v-icon :color="infoIconColor">{{ infoIcon }}</v-icon>
                        </v-col>
                        <v-col :key="`${item}_text`" class="pl-3 pb-3" cols="11">
                            <span class="text-body-1">{{ item }}</span>
                        </v-col>
                    </template>
                </v-row>
            </base-form-container>
            <!-- Actions -->
            <base-form-actions-container :loading="loading">
                <base-form-actions
                    :hide-primary-button="hidePrimaryButton"
                    :hide-secondary-button="hideSecondaryButton"
                    :loading="saving"
                    :primary-button-color="primaryButtonColor"
                    :primary-button-text="primaryButtonText"
                    :secondary-button-color="secondaryButtonColor"
                    :secondary-button-text="secondaryButtonText"
                    @close="handleSecondaryFormEvent"
                    @submit="handleConfirmSubmit"
                ></base-form-actions>
            </base-form-actions-container>
        </v-card>
    </base-dialog>
</template>

<script>
    import {isArray, isEmpty} from 'underscore'
    // Mixins
    import BaseFormMixin from '../mixins/base-form.mixin'
    // Components
    import BaseDialog from './BaseDialog'

    /**
     * Dialog card form used to confirm actions.  Accepts a callback for submit and close
     */
    export default {
        name: 'BaseConfirmDialog',
        mixins: [BaseFormMixin],
        props: {
            /**
             * Toggle the visibility of the primary button
             */
            hidePrimaryButton: {
                type: Boolean,
                default: false
            },
            /**
             * Toggle the visibility of the secondary button
             */
            hideSecondaryButton: {
                type: Boolean,
                default: false
            },
            /**
             * Optional information to display.  Can be a single string, or an array of strings
             */
            info: {
                type: [String, Array],
                default: ''
            },
            /**
             * The icon displayed to the left of the additional info
             */
            infoIcon: {
                type: String,
                default: 'info'
            },
            /**
             * The color of the icon displayed to the left of the additional info
             */
            infoIconColor: {
                type: String,
                default: 'info'
            },
            /**
             * The max width of the dialog component
             */
            maxWidth: {
                type: [Number, String],
                default: 'none'
            },
            /**
             * The color of the primary button
             */
            primaryButtonColor: {
                type: String,
                default: 'primary'
            },
            /**
             * The text of the primary button
             */
            primaryButtonText: {
                type: String,
                default: 'Confirm'
            },
            /**
             * The color of the secondary button
             */
            secondaryButtonColor: {
                type: String,
                default: 'secondary'
            },
            /**
             * The text of the secondary button
             */
            secondaryButtonText: {
                type: String,
                default: 'Close'
            },
            /**
             * Submit a specific key from the item
             */
            submitKey: {
                type: String,
                default: ''
            },
            /**
             * Combine with the submitKey prop. to pass back an object on submit i.e.{[submitKey]: submitValue}
             */
            submitValue: {
                type: String,
                default: ''
            },
            /**
             * The main text displayed in the confirm
             */
            text: {
                type: String,
                default: ''
            },
            /**
             * The icon displayed to the left of the main text
             */
            textIcon: {
                type: String,
                default: 'warning'
            },
            /**
             * The color of the icon displayed to the left of the main text
             */
            textIconColor: {
                type: String,
                default: 'primary'
            },
            /**
             * The title of the confirm
             */
            title: {
                type: String,
                default: 'Confirm'
            },
            /**
             * Toggle the visibility of the component
             */
            value: {
                type: Boolean,
                default: false
            }
        },
        components: {
            BaseDialog
        },
        computed: {
            infoItems () {
                const vm = this

                if (isEmpty(vm.info)) {
                    return []
                }

                if (isArray(vm.info)) {
                    return vm.info
                }

                return [vm.info]
            }
        },
        methods: {
            handleConfirmSubmit () {
                const vm = this
                let response = vm.item

                if (!isArray(response)) {
                    if (vm.submitKey && !vm.submitValue) {
                        response = vm.item[vm.submitKey]
                    }

                    if (!vm.submitKey && vm.submitValue) {
                        response = vm.item[vm.submitValue]
                    }

                    if (vm.submitKey && vm.submitValue) {
                        response = {[vm.submitKey]: vm.item[vm.submitValue]}
                    }
                }

                vm.handlePrimaryFormEvent(response)
            }
        }
    }

</script>
